import {
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';
import { useTranslations } from 'use-intl';

import { noop } from '@blockpulse3/data/shared';
import {
  CreateSubscriptionInput,
  CreateSubscriptionMutation,
  IdentityType,
  SubscriptionInfosFragment,
  UpdateSubscriptionMutation,
  useCreateSubscriptionMutation,
  useUpdateSubscriptionMutation,
} from '@blockpulse3/graphql/hooks';
import { ResponsiveModal, ResponsiveModalProps, useErrorToast } from '@blockpulse3/ui/commons';

import { isLegalSubscriber } from '../utils';
import { SubscriberInformationsForm } from './Forms/SubscriberInformationsForm';
import { ICrowdfundingSubscribers, IFundraisingSubscriber } from './types';

type Props = {
  fundraisingId: string;
  subscriptionId?: string;
  isCrowdfunding?: boolean;
  isEditing: boolean;
  defaultValues: IFundraisingSubscriber | ICrowdfundingSubscribers;
  onClose: () => void;
  onComplete: (
    data: CreateSubscriptionMutation | UpdateSubscriptionMutation,
    subscription: CreateSubscriptionInput,
    isLegal: boolean,
  ) => void;
  setInvalidLegalSubscriber?: (
    company: SubscriptionInfosFragment['identity']['companyIdentity'] | undefined,
  ) => void;
} & Omit<ResponsiveModalProps, 'children' | 'onClose'>;

/**
 * FundraisingSubscribersModal.
 * Form for the Parameters informations of a Fundraising.
 *
 * @param {Props}
 * @returns {JSX.Element}
 */
export function FundraisingSubscribersModal<
  T extends IFundraisingSubscriber | ICrowdfundingSubscribers,
>({
  fundraisingId,
  subscriptionId,
  isCrowdfunding = false,
  isEditing,
  defaultValues,
  onClose,
  onComplete,
  setInvalidLegalSubscriber = noop,
  ...props
}: Props): JSX.Element {
  const i18nFundraisingSubscribersModal = useTranslations('FundraisingSubscribersModal');
  const i18nFundraisingSubscriberForm = useTranslations('FundraisingSubscribersForm');

  const errorToast = useErrorToast();

  const [createSubscription] = useCreateSubscriptionMutation();
  const [updateSubscription] = useUpdateSubscriptionMutation();

  const handleModalClose = (): void => {
    onClose();
  };

  const handleFormSubmit = (data: T): void => {
    setInvalidLegalSubscriber(undefined);
    const { subscriber } = data;
    const isLegal = isLegalSubscriber(subscriber);

    const subscription = {
      type: isLegal ? IdentityType.COMPANY : IdentityType.INDIVIDUAL,
      name: isLegal ? subscriber.name : null,
      registrationNumber: isLegal ? subscriber.identificationNumber : null,
      firstName: !isLegal ? subscriber.firstName : null,
      lastName: !isLegal ? subscriber.lastName : null,
      email: subscriber.email.toLowerCase(),
      investAmount: 'amount' in subscriber ? subscriber.amount : 0,
      admissionFees: subscriber.admissionFees,
      minimalAmount: subscriber.minimalAmount,
      maximalAmount: subscriber.maximalAmount,
    };

    if (subscriptionId && subscriptionId !== '') {
      updateSubscription({
        variables: {
          updateSubscriptionInput: {
            subscriptionId,
            ...subscription,
          },
        },
        onCompleted: (data) => {
          onComplete(data, subscription, isLegal);
        },
        onError: () => {
          errorToast({ title: i18nFundraisingSubscriberForm('Toast.Error') });
        },
      });
    } else if (fundraisingId) {
      createSubscription({
        variables: {
          fundraisingId,
          createSubscriptionInput: { ...subscription },
        },
        onCompleted: (data) => {
          onComplete(data, subscription, isLegal);
        },
        onError: () => {
          errorToast({ title: i18nFundraisingSubscriberForm('Toast.Error') });
        },
      });
    }
    handleModalClose();
  };

  return (
    <ResponsiveModal onClose={handleModalClose} {...props}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{i18nFundraisingSubscribersModal('Title')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <SubscriberInformationsForm
            defaultValues={defaultValues}
            isCrowdfunding={isCrowdfunding}
            isEditing={isEditing}
            onClose={handleModalClose}
            onSubmit={handleFormSubmit}
          />
        </ModalBody>
      </ModalContent>
    </ResponsiveModal>
  );
}
